import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as PieTooltip,
} from 'recharts';
import styles from './DataQualityPieChart.styles';
import { EMPTY_PIE, InlineLegend, CHART_COLORS, LABEL_MAP } from '../../utils/dataQuality.helpers';
import { buildPieBreakdown } from '../../utils/dataQualityPie.helpers';

export default function DataQualityPieChart({ data = [], colors = CHART_COLORS }) {
  const { seriesDecimals, seriesDisplayInts } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return { seriesDecimals: EMPTY_PIE, seriesDisplayInts: EMPTY_PIE };
    return buildPieBreakdown(data);
  }, [data]);

  const displayMap = useMemo(() => {
    return Object.fromEntries((seriesDisplayInts || []).map((s) => [s.name, s.value]));
  }, [seriesDisplayInts]);

  const legendEntries = (seriesDecimals || []).map((s, i) => ({
    label: LABEL_MAP[s.name] || s.name,
    color: colors[i % colors.length],
  }));

  return (
    <Box sx={{ ...styles.card, ...styles.chartRoot, ...styles.chartText }}>
      <Box sx={styles.headerRow}>
        <Typography variant="subtitle2" sx={styles.headerTitle}>Overall Split</Typography>
      </Box>

      <Box sx={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 6, left: 6, bottom: 4 }}>
            <Pie
              data={seriesDecimals}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="85%"
              innerRadius="40%"
              labelLine={false}
              label={({ cx, cy, midAngle, innerRadius, outerRadius, name }) => {
                const value = displayMap[name] ?? 0;
                if (!value) return null;
                const RAD = Math.PI / 180;
                const rIn = parseFloat(innerRadius);
                const rOut = parseFloat(outerRadius);
                const radius = rIn + (rOut - rIn) * 0.5;
                const x = cx + radius * Math.cos(-midAngle * RAD);
                const y = cy + radius * Math.sin(-midAngle * RAD);
                return (
                  <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" style={{ fontSize: '13px' }}>
                    {`${value}%`}
                  </text>
                );
              }}
            >
              {seriesDecimals.map((s, i) => (
                <Cell key={s.name} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <PieTooltip
              formatter={(raw, name) => [`${displayMap[name] ?? Math.round(raw)}%`, LABEL_MAP[name] || name]}
              wrapperStyle={{ fontSize: 10, padding: '4px 8px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <InlineLegend entries={legendEntries} styles={styles} />
    </Box>
  );
}
